import { AiLabel } from '@spa/pages-converters/cms/components/convertCmsImage';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { ImageMimeTypes } from '@spa/lib/cms/const';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ReactElement } from 'react';
import classNames from 'clsx';
import configEnv from '../../lib/image-loader/imageConfigEnv';
import { preload } from 'react-dom';
import styles from './banner-picture.component.module.scss';
import useImagesLoader from '@spa/lib/react/hooks/useImagesLoader';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface Props {
  /** Picture data */
  readonly picture: BannerPicture;
  readonly priority: boolean | null;
  readonly enableNextJsLoader?: boolean;
  readonly fixedBreakPoint?: PictureBreakpoints;
  readonly scaleImageBeyondIntrinsicDimensions?: boolean;
  readonly aiMarkerClassName?: string;
}

export interface BannerPicture {
  readonly imageDesktop: Image;
  readonly imageMobile: Image | null;
}

export interface Image {
  height: number;
  id: string;
  src: string;
  mimeType: string;
  width: number;
  alt?: string;
  aiLabel?: AiLabel;
}

export enum PictureBreakpoints {
  MOBILE = 767,
  TABLET = 1023
}

// One class per side of each supported breakpoint; the media queries mirror the <source>s.
const AI_MARKER_BREAKPOINT_CLASSES: Record<
  PictureBreakpoints,
  { readonly mobile: string; readonly desktop: string }
> = {
  [PictureBreakpoints.MOBILE]: {
    mobile: styles['picture__ai-marker--max-767'],
    desktop: styles['picture__ai-marker--min-768']
  },
  [PictureBreakpoints.TABLET]: {
    mobile: styles['picture__ai-marker--max-1023'],
    desktop: styles['picture__ai-marker--min-1024']
  }
};

const allSizes = configEnv.imageSizes.concat(configEnv.deviceSizes).sort((a, b) => a - b);

const emptyTransparent1x1GifData =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export default function BannerPictureComponent({
  picture,
  priority,
  enableNextJsLoader = true,
  fixedBreakPoint,
  scaleImageBeyondIntrinsicDimensions = true,
  aiMarkerClassName
}: Props): ReactElement {
  const { imageDesktop, imageMobile } = picture;
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('image-ai-marker');

  const imageMobileOrDefault = imageMobile || imageDesktop;

  const imagesLoader = useImagesLoader(enableNextJsLoader);

  function getSrcSet(image: Image): string {
    let returnValue = '';

    let nextWidth = image.width;
    for (const imageSize of allSizes) {
      if (imageSize >= nextWidth) {
        nextWidth = imageSize;
        break;
      }
    }

    returnValue = allSizes
      .filter((size) => size <= nextWidth)
      .map((imageSize) => {
        return `${imagesLoader({
          src: image.src,
          width: imageSize,
          quality: 75
        })} ${imageSize}w`;
      })
      .join(', ');

    return returnValue;
  }

  const srcSetDesktop = getSrcSet(imageDesktop);
  const srcSetMobileOrDefault = getSrcSet(imageMobileOrDefault);

  if (priority) {
    if (imageMobileOrDefault) {
      const mobileHref = imagesLoader({
        src: imageMobileOrDefault.src,
        width: imageMobileOrDefault.width,
        quality: 75
      });
      preload(mobileHref, {
        as: 'image',
        imageSrcSet: srcSetMobileOrDefault,
        media: `(max-width: ${imageMobileOrDefault.width}px)`,
        fetchPriority: priority ? 'high' : undefined
      });
    }

    const desktopRef = imagesLoader({
      src: imageDesktop.src,
      width: imageDesktop.width,
      quality: 75
    });
    preload(desktopRef, {
      as: 'image',
      imageSrcSet: srcSetDesktop,
      media: `(min-width: ${imageMobileOrDefault?.width ?? 0}px)`,
      fetchPriority: priority ? 'high' : undefined
    });
  }

  const mobileMedia = imageMobileOrDefault
    ? `(max-width: ${fixedBreakPoint ?? imageMobileOrDefault.width}px)`
    : '';

  const isSvg = imageMobileOrDefault?.mimeType === ImageMimeTypes.SVG;

  // Known limitation: alt/data-* live on the single fallback <img>, not per <source>,
  // so only imageMobileOrDefault's aiLabel can be reflected there. The visible marker is
  // rendered per breakpoint asset below, whenever the breakpoint is a fixed one.
  const aiLabelEnabled = imageMobileOrDefault.aiLabel?.enabled === true;
  const imageAlt = imageMobileOrDefault.alt ?? '';
  const isDisclosureInAlt = aiLabelEnabled && imageAlt !== '';
  const displayAlt = isDisclosureInAlt
    ? `${imageAlt}, ${formatMessage('accessibleLabel')}`
    : imageAlt;
  // A marker stays aria-hidden only when the fallback alt already announces the disclosure;
  // otherwise (decorative image, or an AI asset other than the fallback) it announces itself.
  const aiMarkerAlt = isDisclosureInAlt ? imageAlt : '';

  const digitalSourceTypeAttr = aiLabelEnabled
    ? { 'data-digital-source-type': 'compositeWithTrainedAlgorithmicMedia' }
    : {};

  const breakpointClasses =
    imageMobile && fixedBreakPoint !== undefined
      ? AI_MARKER_BREAKPOINT_CLASSES[fixedBreakPoint]
      : undefined;
  const aiMarkerCandidates: ReadonlyArray<{
    readonly key: string;
    readonly image: Image;
    readonly className?: string;
  }> = breakpointClasses
    ? [
        { key: 'mobile', image: imageMobileOrDefault, className: breakpointClasses.mobile },
        { key: 'desktop', image: imageDesktop, className: breakpointClasses.desktop }
      ]
    : [{ key: 'default', image: imageMobileOrDefault }];
  const aiMarkers = aiMarkerCandidates.filter(({ image }) => image.aiLabel?.enabled === true);

  return (
    <div className={styles.picture} data-testid="banner-picture-component">
      {aiMarkers.map(({ key, image, className }) => (
        <ImageAiMarkerComponent
          key={key}
          show={image.aiLabel?.enabled}
          position={image.aiLabel?.position}
          style={image.aiLabel?.style}
          alt={aiMarkerAlt}
          className={classNames(className, aiMarkerClassName)}
        />
      ))}
      {isSvg ? (
        <ImageComponent
          src={imageMobileOrDefault?.src ?? emptyTransparent1x1GifData}
          fetchPriority={priority ? 'high' : undefined}
          width={imageMobileOrDefault.width}
          height={imageMobileOrDefault.height}
          alt={displayAlt}
          loading={priority ? 'eager' : 'lazy'}
          mimeType={imageMobileOrDefault.mimeType}
          className={classNames(styles['picture__element'], {
            [styles['picture__element--full-width']]: scaleImageBeyondIntrinsicDimensions
          })}
          {...digitalSourceTypeAttr}
        />
      ) : (
        <picture className={styles.picture__wrapper}>
          {imageMobileOrDefault && (
            <source
              key={imageMobileOrDefault.id + '1'}
              srcSet={srcSetMobileOrDefault}
              width={imageMobileOrDefault.width}
              height={imageMobileOrDefault.height}
              media={mobileMedia}
            />
          )}

          <source
            key={imageDesktop.id + '2'}
            srcSet={srcSetDesktop}
            width={imageDesktop.width}
            height={imageDesktop.height}
            media={''}
          />

          <ImageComponent
            src={imageMobileOrDefault?.src ?? emptyTransparent1x1GifData}
            fetchPriority={priority ? 'high' : undefined}
            width={imageMobileOrDefault.width}
            height={imageMobileOrDefault.height}
            alt={displayAlt}
            loading={priority ? 'eager' : 'lazy'}
            mimeType={imageMobileOrDefault.mimeType}
            className={classNames(styles['picture__element'], {
              [styles['picture__element--full-width']]: scaleImageBeyondIntrinsicDimensions
            })}
            {...digitalSourceTypeAttr}
          />
        </picture>
      )}
    </div>
  );
}
